import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import { INSIGHTS } from "@/constants/insights";
import { SITE } from "@/constants/site";
import NotFound from "@/pages/NotFound";

const DOMAIN = "https://www.vrahmedabadrealtors.com";

export default function InsightArticlePage() {
  const { slug: articleSlug } = useParams();
  const article = INSIGHTS.find((a) => a.slug === articleSlug);

  useEffect(() => {
    if (!article) return;
    document.title = article.title;
    const meta = document.querySelector("meta[name='description']");
    const prev = meta?.content;
    if (meta) meta.content = article.meta;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-ld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.h1,
      description: article.meta,
      author: { "@type": "Organization", name: "VR Ahmedabad Realtors", url: DOMAIN },
      publisher: {
        "@type": "Organization",
        name: "VR Ahmedabad Realtors",
        logo: { "@type": "ImageObject", url: `${DOMAIN}/vr-logo.jpeg` },
      },
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: `${DOMAIN}/insights/${article.slug}`,
    });
    document.head.appendChild(ld);
    window.scrollTo(0, 0);
    return () => {
      if (meta && prev) meta.content = prev;
      document.getElementById("article-ld")?.remove();
    };
  }, [article]);

  if (!article) return <NotFound />;

  return (
    <main data-testid={`insight-article-${article.slug}`} className="bg-cream font-sans text-slate-800 antialiased">
      <section className="grain bg-forest py-20">
        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-12">
          <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-xs text-white/50">
            <Link to="/" className="transition-colors duration-200 hover:text-gold">Home</Link>
            <CaretRight size={10} />
            <Link to="/insights" className="transition-colors duration-200 hover:text-gold">Insights</Link>
            <CaretRight size={10} />
            <span className="text-gold">{article.h1}</span>
          </nav>
          <h1 className="max-w-3xl font-serif text-3xl tracking-tighter text-white sm:text-4xl lg:text-5xl">
            {article.h1}
          </h1>
          <p className="mt-5 text-xs text-white/50">
            By <span className="text-gold">VR Ahmedabad Realtors — Advisory Team</span> · Published {article.date} · {article.readTime}
          </p>
        </div>
      </section>

      <article className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          {article.sections.map((s) => (
            <section key={s.h2} className="mb-10 last:mb-0">
              <h2 className="font-serif text-xl tracking-tight text-slate-900 sm:text-2xl">{s.h2}</h2>
              {s.paras.map((p) => (
                <p key={p.slice(0, 24)} className="mt-4 text-base leading-relaxed text-slate-600">
                  {p}
                </p>
              ))}
            </section>
          ))}

          <div className="mt-14 rounded-lg border border-black/5 bg-cream p-8">
            <h2 className="font-serif text-xl text-slate-900">Discuss this with an advisor</h2>
            <p className="mt-3 text-sm text-slate-600">
              Call or WhatsApp {SITE.phoneDisplay}, or send us your requirement — we'll respond with specifics, not a
              sales script.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="article-contact-cta"
                className="rounded-sm bg-forest px-6 py-3 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-forest-hover"
              >
                Contact Us
              </Link>
            </div>
            <div className="mt-8 border-t border-black/5 pt-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-forest/70">Related</p>
              <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                {article.related.map((r) => (
                  <Link key={r.href} to={r.href} className="text-sm font-medium text-forest transition-colors duration-200 hover:text-gold">
                    {r.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
